import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { X, MapPin, RefreshCw, Power, PlugZap, Download, Cpu, Terminal, AlertTriangle, SendHorizonal, Radio, CheckCheck, Clock, Wifi, CalendarClock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import EngineCutDialog from "./EngineCutDialog";
import { useAuth } from "@/context/AuthContext";

const money = (n) => Math.round(n || 0).toLocaleString("fr-FR");
const noumea = (ts) => (ts ? new Date(ts).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const IO_NAMES = {
  "78": "iButton Dallas", "207": "RFID", "85": "RPM moteur", "89": "Carburant %", "32": "Température",
  "87": "Km total (CAN)", "83": "Carburant consommé", "82": "Accélérateur", "81": "Vitesse CAN",
  "84": "Carburant (L)", "239": "Contact (ignition)", "240": "Mouvement", "66": "Tension externe",
  "67": "Batterie", "21": "Signal GSM", "24": "Vitesse GPS", "16": "Odomètre total", "80": "Type données",
};

const Stat = ({ label, value, unit }) => (
  <div className="bg-secondary/50 border border-border rounded-md px-3 py-2">
    <p className="label-mono">{label}</p>
    <p className="data text-sm font-bold mt-0.5">{value === null || value === undefined || value === "" ? <span className="text-muted-foreground font-normal">N/D</span> : value}{value !== null && value !== undefined && unit ? <span className="text-muted-foreground text-xs font-normal"> {unit}</span> : null}</p>
  </div>
);

const StatusBadge = ({ c }) => {
  if (c.status === "acked") return <span className="data text-[10px] px-1.5 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] flex items-center gap-1" data-testid={`cmd-status-${c.id}`}><CheckCheck className="w-3 h-3" /> acquittée</span>;
  if (c.transmitted) return <span className="data text-[10px] px-1.5 py-0.5 rounded-full bg-[#17A2B8]/20 text-[#2ec5db] flex items-center gap-1" data-testid={`cmd-status-${c.id}`}><Wifi className="w-3 h-3" /> transmise</span>;
  return <span className="data text-[10px] px-1.5 py-0.5 rounded-full bg-[#F5821F]/20 text-[#F5821F] flex items-center gap-1" data-testid={`cmd-status-${c.id}`}><Clock className="w-3 h-3" /> en file</span>;
};

export default function UnitControlModal({ vehicle, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCv200 = vehicle.model === "CV200" || vehicle.protocol?.startsWith("Queclink");
  const canImmobilize = isAdmin || !!user?.permissions?.immobilizer;
  const [tab, setTab] = useState("commandes");
  const [commands, setCommands] = useState([]);
  const [can, setCan] = useState(null);
  const [cut, setCut] = useState(vehicle.engine_cut || false);
  const [busy, setBusy] = useState(false);
  const [cutDialog, setCutDialog] = useState(null); // 'cut' | 'restore' | null
  const [customCmd, setCustomCmd] = useState("");
  const [cutCmd, setCutCmd] = useState(vehicle.cut_cmd || "setdigout 1");
  const [restoreCmd, setRestoreCmd] = useState(vehicle.restore_cmd || "setdigout 0");
  const [ibuttonCmd, setIbuttonCmd] = useState(vehicle.ibutton_cmd || "readio 78");
  const [ecoCmd, setEcoCmd] = useState(vehicle.eco_cmd || "getparam 11000");
  const [lastPacket, setLastPacket] = useState(null);
  const [sos, setSos] = useState({
    sos_enabled: vehicle.sos_enabled || false,
    sos_input: vehicle.sos_input || "2",
    sos_label: vehicle.sos_label || "Bouton SOS",
    sos_email_enabled: vehicle.sos_email_enabled || false,
  });
  const [sosEmails, setSosEmails] = useState((vehicle.sos_emails || []).join("\n"));
  const [sched, setSched] = useState({
    enabled: vehicle.cut_schedule_enabled || false,
    on_time: vehicle.cut_schedule_on || "22:00",
    off_time: vehicle.cut_schedule_off || "06:00",
    days: vehicle.cut_schedule_days || [],
  });
  const [priv, setPriv] = useState({
    enabled: vehicle.privacy_enabled || false,
    input: vehicle.privacy_input || "0",
    label: vehicle.privacy_label || "Mode vie privée",
  });
  const [privActive, setPrivActive] = useState(vehicle.privacy_active || false);
  const feedRef = useRef(null);

  const saveSched = async () => {
    try { await api.put(`/vehicles/${vehicle.id}/cut-schedule`, sched); toast.success("Coupure programmée enregistrée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur (réservé admin ?)"); }
  };
  const savePrivacy = async () => {
    try { await api.put(`/vehicles/${vehicle.id}/privacy-config`, priv); toast.success("Configuration vie privée enregistrée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur (réservé admin ?)"); }
  };
  const togglePrivacyNow = async () => {
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/privacy`, { active: !privActive });
      setPrivActive(r.data.privacy_active);
      toast.success(r.data.privacy_active ? "Mode vie privée activé — suivi suspendu" : "Mode vie privée désactivé — suivi repris");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggleDay = (d) => setSched((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d] }));

  const saveSos = async () => {
    try {
      const emails = sosEmails.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      await api.put(`/vehicles/${vehicle.id}/sos-config`, { ...sos, sos_emails: emails });
      toast.success("Configuration SOS enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur (réservé admin ?)"); }
  };

  const saveCmdConfig = async () => {
    try { await api.put(`/vehicles/${vehicle.id}/command-config`, { cut_cmd: cutCmd, restore_cmd: restoreCmd, ibutton_cmd: ibuttonCmd, eco_cmd: ecoCmd }); toast.success("Commandes enregistrées"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur (réservé admin ?)"); }
  };

  const loadLastPacket = async () => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}/raw?hours=168`);
      if (!r.data.length) return toast.error("Aucune donnée récente");
      setLastPacket(r.data[0]);
    } catch { toast.error("Échec du chargement"); }
  };

  const loadCommands = useCallback(() => api.get(`/vehicles/${vehicle.id}/commands`).then((r) => setCommands(r.data)).catch(() => {}), [vehicle.id]);
  const loadCan = useCallback(() => api.get(`/vehicles/${vehicle.id}/canbus`).then((r) => { setCan(r.data); setCut(r.data.engine_cut); }).catch(() => {}), [vehicle.id]);
  useEffect(() => { loadCommands(); loadCan(); const t = setInterval(loadCommands, 3000); return () => clearInterval(t); }, [loadCommands, loadCan]);

  const send = async (type, note = "", extra = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/command`, { type, note, ...extra });
      setCut(r.data.engine_cut);
      toast.success(r.data.transmitted ? "Commande transmise au boîtier (GPRS Codec 12) ✓" : "Boîtier hors ligne — commande mise en file, transmise à la reconnexion");
      loadCommands();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const sendCustom = () => {
    const t = customCmd.trim();
    if (!t) return;
    setCustomCmd("");
    send("custom", t);
  };

  const toggleCut = () => setCutDialog(cut ? "restore" : "cut");
  const confirmCut = (payload = {}) => { const a = cutDialog; setCutDialog(null); send(a === "cut" ? "cut_engine" : "restore_engine", "", payload); };

  const exportData = async (fmt) => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}/raw?hours=24`);
      const rows = r.data;
      if (!rows.length) return toast.error("Aucune donnée sur 24h");
      let blob;
      if (fmt === "json") {
        blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
      } else {
        const flatten = (p) => {
          const out = {};
          for (const [k, v] of Object.entries(p)) {
            if ((k === "io" || k === "can") && v && typeof v === "object") {
              for (const [ik, iv] of Object.entries(v)) out[`${k}_${ik}`] = iv;
            } else if (v !== null && typeof v === "object") {
              out[k] = JSON.stringify(v);
            } else {
              out[k] = v;
            }
          }
          return out;
        };
        const flatRows = rows.map(flatten);
        const cols = Array.from(flatRows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
        const esc = (val) => {
          const str = String(val ?? "");
          return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const csv = [cols.join(",")].concat(flatRows.map((r) => cols.map((c) => esc(r[c])).join(","))).join("\n");
        blob = new Blob([csv], { type: "text/csv" });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(vehicle.plate || vehicle.name || vehicle.id).replace(/\s/g, "")}_raw_24h.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} (${rows.length} points)`);
    } catch { toast.error("Échec de l'export"); }
  };

  const TABS_ALL = [{ k: "commandes", l: "Commandes", i: Terminal }, { k: "canbus", l: "Télémétrie", i: Cpu }, { k: "sos", l: "SOS", i: AlertTriangle }, { k: "auto", l: "Auto", i: CalendarClock }, { k: "brut", l: "Données brutes", i: Download }];
  const TABS = (isAdmin ? TABS_ALL : TABS_ALL.filter((t) => t.k === "commandes"))
    .filter((t) => !isCv200 || !["sos", "auto"].includes(t.k));
  const feed = [...commands].reverse();

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="control-modal">
        <div className="flex items-center justify-between mb-1">
          <div><p className="label-mono">Contrôle unité</p><h3 className="font-bold text-lg">{vehicle.name} <span className="text-muted-foreground text-sm data">· {vehicle.plate}</span></h3></div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 mb-4 bg-secondary/40 p-1 rounded-md">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`control-tab-${t.k}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded text-xs font-semibold transition-colors ${tab === t.k ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <t.i className="w-3.5 h-3.5" /> {t.l}
            </button>
          ))}
        </div>

        {tab === "commandes" && isCv200 && (
          <p className="text-sm text-muted-foreground">Le suivi et les événements CV200 sont reçus automatiquement. Les commandes à distance, le direct vidéo et les extraits vidéo ne sont pas encore disponibles sur cette plateforme.</p>
        )}
        {tab === "commandes" && !isCv200 && (
          <div className="space-y-4">
            {canImmobilize && (
            <div className={`rounded-lg p-4 border ${cut ? "bg-destructive/15 border-destructive/40" : "bg-secondary/40 border-border"}`} data-testid="immobilizer-box">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm flex items-center gap-2"><Power className={`w-4 h-4 ${cut ? "text-destructive" : "text-[#10B981]"}`} /> Bloquer le prochain démarrage</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{cut ? "Le prochain démarrage est actuellement BLOQUÉ." : "Le véhicule peut démarrer normalement."}</p>
                </div>
                <button onClick={toggleCut} disabled={busy} data-testid="engine-cut-toggle"
                  className={`px-4 py-2 rounded-md text-sm font-bold transition-colors ${cut ? "bg-[#10B981] hover:bg-[#0ea371] text-white" : "bg-destructive hover:bg-red-600 text-white"}`}>
                  {cut ? "Autoriser" : "Bloquer"}
                </button>
              </div>
            </div>
            )}

            {!isAdmin && (
              <button onClick={() => send("eco_check")} disabled={busy} data-testid="cmd-eco-check-client" className="w-full flex items-center gap-2 justify-center bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-40 text-white rounded-md py-2.5 text-sm font-semibold transition-colors"><Cpu className="w-4 h-4" /> Vérifier l'éco-conduite</button>
            )}

            {isAdmin && (<>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => send("get_position")} disabled={busy} data-testid="cmd-get-position" className="flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border rounded-md py-2.5 text-sm font-semibold"><MapPin className="w-4 h-4 brand-teal" /> Demander position</button>
              <button onClick={() => send("restart_device")} disabled={busy} data-testid="cmd-restart" className="flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border rounded-md py-2.5 text-sm font-semibold"><RefreshCw className="w-4 h-4 brand-teal" /> Redémarrer boîtier</button>
              <button onClick={() => send("output_on")} disabled={busy} data-testid="cmd-output-on" className="flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border rounded-md py-2.5 text-sm font-semibold"><PlugZap className="w-4 h-4 brand-teal" /> Activer sortie</button>
              <button onClick={() => send("output_off")} disabled={busy} data-testid="cmd-output-off" className="flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border rounded-md py-2.5 text-sm font-semibold"><PlugZap className="w-4 h-4 text-muted-foreground" /> Désactiver sortie</button>
            </div>

            <button onClick={() => send("ibutton")} disabled={busy} data-testid="cmd-ibutton" className="w-full flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border rounded-md py-2.5 text-sm font-semibold"><Radio className="w-4 h-4 brand-teal" /> Lire / activer iButton (conducteur)</button>
            </>)}

            {/* Fenêtre de dialogue — console de commandes temps réel */}
            <div className="rounded-lg border border-border bg-secondary/20 overflow-hidden" data-testid="command-console">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-secondary/40">
                <Terminal className="w-3.5 h-3.5 brand-teal" />
                <p className="label-mono flex-1">Console de dialogue boîtier</p>
                <span className="text-[10px] text-muted-foreground data">GPRS · Codec 12</span>
              </div>
              <div ref={feedRef} className="h-56 overflow-y-auto p-3 space-y-2.5" data-testid="command-feed">
                {feed.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-1">
                    <SendHorizonal className="w-6 h-6 opacity-40" />
                    <p className="text-xs">Aucun échange. Envoyez une commande ci-dessous.</p>
                  </div>
                )}
                {feed.map((c) => (
                  <div key={c.id} className="space-y-1.5" data-testid={`cmd-log-${c.id}`}>
                    {/* Message envoyé (droite) */}
                    <div className="flex justify-end">
                      <div className="max-w-[80%] bg-[#17A2B8] text-white rounded-2xl rounded-br-sm px-3 py-2 shadow-sm">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <SendHorizonal className="w-3 h-3 opacity-90" />
                          <span className="text-[11px] font-bold">{c.label}</span>
                        </div>
                        {c.command_text ? <p className="data text-[11px] bg-black/20 rounded px-1.5 py-0.5 inline-block">{c.command_text}</p> : null}
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <span className="data text-[9px] opacity-80">{noumea(c.created_at)}</span>
                          <StatusBadge c={c} />
                        </div>
                      </div>
                    </div>
                    {/* Réponse reçue (gauche) */}
                    {c.response && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] bg-secondary border border-border rounded-2xl rounded-bl-sm px-3 py-2" data-testid={`cmd-resp-${c.id}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Radio className="w-3 h-3 text-[#10B981]" />
                            <span className="text-[11px] font-bold text-[#10B981]">Réponse boîtier</span>
                          </div>
                          <p className="data text-[11px] break-all">{c.response}</p>
                          {c.acked_at && <span className="data text-[9px] text-muted-foreground">{noumea(c.acked_at)}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Saisie commande personnalisée */}
              {isAdmin && (
              <div className="flex items-center gap-2 p-2 border-t border-border bg-secondary/40">
                <input value={customCmd} onChange={(e) => setCustomCmd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendCustom(); }}
                  placeholder="Commande FMC130 (ex: getgps, getver, setdigout 1)"
                  data-testid="cmd-custom-input"
                  className="flex-1 bg-background/60 border border-border rounded-full px-3 py-2 text-xs data outline-none focus:border-[#17A2B8]" />
                <button onClick={sendCustom} disabled={busy || !customCmd.trim()} data-testid="cmd-custom-send"
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-40 text-white transition-colors">
                  <SendHorizonal className="w-4 h-4" />
                </button>
              </div>
              )}
            </div>

            {/* Configuration commande de coupure */}
            {isAdmin && (
            <div className="rounded-lg p-3 border border-border bg-secondary/30" data-testid="cmd-config-box">
              <p className="label-mono mb-2 flex items-center gap-1"><Cpu className="w-3.5 h-3.5" /> Commande de coupure (FMC130)</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Couper<input value={cutCmd} onChange={(e) => setCutCmd(e.target.value)} data-testid="cfg-cut-cmd" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs data outline-none focus:border-[#17A2B8]" /></label>
                <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Rétablir<input value={restoreCmd} onChange={(e) => setRestoreCmd(e.target.value)} data-testid="cfg-restore-cmd" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs data outline-none focus:border-[#17A2B8]" /></label>
              </div>
              <label className="text-[11px] text-muted-foreground flex flex-col gap-1 mt-2">Commande iButton (identification conducteur)<input value={ibuttonCmd} onChange={(e) => setIbuttonCmd(e.target.value)} data-testid="cfg-ibutton-cmd" placeholder="ex: readio 78" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs data outline-none focus:border-[#17A2B8]" /></label>
              <label className="text-[11px] text-muted-foreground flex flex-col gap-1 mt-2">Commande vérification éco-conduite (Green Driving)
                <div className="flex items-center gap-2">
                  <input value={ecoCmd} onChange={(e) => setEcoCmd(e.target.value)} data-testid="cfg-eco-cmd" placeholder="ex: getparam 11000" className="flex-1 bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-xs data outline-none focus:border-[#17A2B8]" />
                  <button onClick={() => send("eco_check")} disabled={busy} data-testid="cmd-eco-check-btn" className="shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-40 text-white transition-colors">Vérifier</button>
                </div>
              </label>
              <p className="text-[10px] text-muted-foreground mt-1.5"><span className="data">getparam 11000</span> renvoie la priorité Green Driving (0 = désactivé, 1/2/3 = activé). La réponse du boîtier s'affiche dans l'historique des commandes ci-dessus.</p>
              <p className="text-[10px] text-muted-foreground mt-1.5">Syntaxe FMC : <span className="data">setdigout 1/0</span> pour la coupure (DOUT câblé). <span className="data">readio 78</span> lit l'I/O iButton (Dallas 1-Wire) ; adaptez selon votre firmware/lecteur RFID.</p>
              <p className="text-[10px] text-muted-foreground mt-1.5">Syntaxe FMC130 : <span className="data">setdigout 1</span> = DOUT1 activé, <span className="data">setdigout 0</span> = désactivé. Adaptez au DOUT câblé à votre relais de coupure (le DOUT doit être libre / non assigné dans la config du boîtier).</p>
              <button onClick={saveCmdConfig} data-testid="cfg-save-btn" className="mt-2 text-xs font-semibold text-[#2ec5db] hover:underline">Enregistrer les commandes</button>
            </div>
            )}
          </div>
        )}

        {tab === "sos" && (
          <div className="space-y-4" data-testid="sos-config">
            <div className="rounded-lg p-4 border border-destructive/30 bg-destructive/10">
              <p className="font-bold text-sm flex items-center gap-2 text-destructive"><AlertTriangle className="w-4 h-4" /> Bouton d'urgence SOS</p>
              <p className="text-xs text-muted-foreground mt-1">Le bouton SOS est câblé sur une entrée numérique (DIN) du FMC130. À l'appui, une alerte critique est levée sur la carte et, si activé, un email est envoyé aux destinataires choisis.</p>
            </div>
            <label className="flex items-center justify-between text-sm font-semibold bg-secondary/40 border border-border rounded-md px-3 py-2.5">
              Activer le bouton SOS
              <input type="checkbox" checked={sos.sos_enabled} onChange={(e) => setSos((s) => ({ ...s, sos_enabled: e.target.checked }))} data-testid="sos-enabled" className="w-4 h-4 accent-[#DC2626]" />
            </label>
            <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Entrée (DIN) du bouton SOS
              <select value={sos.sos_input} onChange={(e) => setSos((s) => ({ ...s, sos_input: e.target.value }))} data-testid="sos-input" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]">
                <option value="1">DIN1 (IO 1)</option>
                <option value="2">DIN2 (IO 2) — recommandé</option>
                <option value="3">DIN3 (IO 3)</option>
              </select>
            </label>
            <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Libellé du bouton
              <input value={sos.sos_label} onChange={(e) => setSos((s) => ({ ...s, sos_label: e.target.value }))} data-testid="sos-label" placeholder="Bouton SOS" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]" />
            </label>
            <label className="flex items-center justify-between text-sm font-semibold bg-secondary/40 border border-border rounded-md px-3 py-2.5">
              Notifier par email
              <input type="checkbox" checked={sos.sos_email_enabled} onChange={(e) => setSos((s) => ({ ...s, sos_email_enabled: e.target.checked }))} data-testid="sos-email-enabled" className="w-4 h-4 accent-[#DC2626]" />
            </label>
            {sos.sos_email_enabled && (
              <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Destinataires — choisissez qui notifier (un email par ligne, ou séparés par des virgules)
                <textarea value={sosEmails} onChange={(e) => setSosEmails(e.target.value)} rows={3} data-testid="sos-emails" placeholder="chef@boomerang.nc, securite@boomerang.nc" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm data outline-none focus:border-[#17A2B8]" />
              </label>
            )}
            <button onClick={saveSos} data-testid="sos-save-btn" className="w-full bg-[#DC2626] hover:bg-red-700 text-white font-bold py-2.5 rounded-md text-sm">Enregistrer la configuration SOS</button>
          </div>
        )}

        {tab === "auto" && (
          <div className="space-y-5" data-testid="auto-config">
            {/* Coupure moteur programmée */}
            <div className="rounded-lg p-4 border border-border bg-secondary/30 space-y-3">
              <p className="font-bold text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 brand-teal" /> Coupure moteur programmée</p>
              <p className="text-[11px] text-muted-foreground">Coupe et rétablit automatiquement le moteur aux heures choisies (heure Nouvelle-Calédonie). Réservé aux administrateurs.</p>
              <label className="flex items-center justify-between text-sm font-semibold bg-secondary/40 border border-border rounded-md px-3 py-2.5">
                Activer la coupure programmée
                <input type="checkbox" checked={sched.enabled} onChange={(e) => setSched((s) => ({ ...s, enabled: e.target.checked }))} data-testid="sched-enabled" className="w-4 h-4 accent-[#17A2B8]" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Heure de coupure
                  <input type="time" value={sched.on_time} onChange={(e) => setSched((s) => ({ ...s, on_time: e.target.value }))} data-testid="sched-on" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm data outline-none focus:border-[#17A2B8]" />
                </label>
                <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Heure de rétablissement
                  <input type="time" value={sched.off_time} onChange={(e) => setSched((s) => ({ ...s, off_time: e.target.value }))} data-testid="sched-off" className="bg-secondary/60 border border-border rounded-md px-2 py-1.5 text-sm data outline-none focus:border-[#17A2B8]" />
                </label>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Jours (aucun sélectionné = tous les jours)</p>
                <div className="flex gap-1 flex-wrap" data-testid="sched-days">
                  {DAYS.map((d, i) => (
                    <button key={i} type="button" onClick={() => toggleDay(i)} data-testid={`sched-day-${i}`}
                      className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${sched.days.includes(i) ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "bg-secondary/50 text-muted-foreground border-border hover:text-foreground"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <button onClick={saveSched} data-testid="sched-save-btn" className="w-full bg-[#17A2B8] hover:bg-[#1391a5] text-white font-bold py-2.5 rounded-md text-sm">Enregistrer la coupure programmée</button>
            </div>

            {/* Mode vie privée */}
            <div className="rounded-lg p-4 border border-border bg-secondary/30 space-y-3">
              <p className="font-bold text-sm flex items-center gap-2"><EyeOff className="w-4 h-4 brand-teal" /> Mode vie privée</p>
              <p className="text-[11px] text-muted-foreground">Suspend le suivi : pendant le mode vie privée, aucune position n'est enregistrée ni affichée. Utile pour les trajets personnels.</p>
              <div className={`flex items-center justify-between rounded-md px-3 py-2.5 border ${privActive ? "bg-[#F5821F]/15 border-[#F5821F]/40" : "bg-secondary/40 border-border"}`}>
                <div>
                  <p className="text-sm font-bold flex items-center gap-2">{privActive ? <EyeOff className="w-4 h-4 text-[#F5821F]" /> : <Eye className="w-4 h-4 text-[#10B981]" />} {privActive ? "Suivi suspendu" : "Suivi actif"}</p>
                  <p className="text-[11px] text-muted-foreground">{privActive ? "Mode vie privée en cours." : "Le véhicule est suivi normalement."}</p>
                </div>
                <button onClick={togglePrivacyNow} data-testid="privacy-toggle-now" className={`px-4 py-2 rounded-md text-sm font-bold text-white transition-colors ${privActive ? "bg-[#10B981] hover:bg-[#0ea371]" : "bg-[#F5821F] hover:bg-[#e0740f]"}`}>{privActive ? "Désactiver" : "Activer"}</button>
              </div>
              <label className="flex items-center justify-between text-sm font-semibold bg-secondary/40 border border-border rounded-md px-3 py-2.5">
                Autoriser un bouton physique (DIN)
                <input type="checkbox" checked={priv.enabled} onChange={(e) => setPriv((p) => ({ ...p, enabled: e.target.checked }))} data-testid="privacy-enabled" className="w-4 h-4 accent-[#F5821F]" />
              </label>
              {priv.enabled && (
                <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Entrée (DIN) du bouton vie privée (appui = bascule)
                  <select value={priv.input} onChange={(e) => setPriv((p) => ({ ...p, input: e.target.value }))} data-testid="privacy-input" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]">
                    <option value="0">Aucune — bascule uniquement depuis l'application</option>
                    <option value="1">DIN1 (IO 1)</option>
                    <option value="2">DIN2 (IO 2)</option>
                    <option value="3">DIN3 (IO 3)</option>
                  </select>
                </label>
              )}
              <label className="text-[11px] text-muted-foreground flex flex-col gap-1">Libellé
                <input value={priv.label} onChange={(e) => setPriv((p) => ({ ...p, label: e.target.value }))} data-testid="privacy-label" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-sm outline-none focus:border-[#17A2B8]" />
              </label>
              <button onClick={savePrivacy} data-testid="privacy-save-btn" className="w-full bg-secondary/60 hover:bg-secondary border border-border font-bold py-2.5 rounded-md text-sm">Enregistrer la configuration vie privée</button>
            </div>
          </div>
        )}

        {tab === "canbus" && can && (          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="canbus-grid">
              <Stat label="Vitesse" value={money(can.speed_kmh)} unit="km/h" />
              <Stat label="Contact" value={can.ignition ? "ON" : "OFF"} />
              <Stat label="Satellites" value={can.satellites} />
              <Stat label="Signal GSM" value={can.gsm_signal} />
              <Stat label="Alimentation" value={can.external_voltage_v} unit="V" />
              <Stat label="Batterie interne" value={can.battery_voltage_v} unit="V" />
              <Stat label="Distance totale" value={money(can.total_distance_km)} unit="km" />
              <Stat label="Heures moteur" value={can.engine_hours} unit="h" />
              <Stat label="Moteur" value={can.engine_cut ? "COUPÉ" : "OK"} />
              <Stat label="Régime" value={can.rpm} unit="tr/min" />
              <Stat label="Temp. liquide" value={can.engine_coolant_temp_c} unit="°C" />
              <Stat label="Niveau carburant" value={can.fuel_level_pct} unit="%" />
              <Stat label="Accélérateur" value={can.throttle_pct} unit="%" />
              <Stat label="Carburant consommé" value={can.fuel_used_l} unit="L" />
              <Stat label="Vitesse CAN" value={can.can_speed_kmh} unit="km/h" />
            </div>
            {!can.has_can && <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#F5821F] shrink-0 mt-0.5" /> Régime, température et carburant (N/D) nécessitent un adaptateur bus CAN câblé sur le FMC130. Les autres valeurs proviennent de la dernière trame réelle du boîtier.</p>}
            <button onClick={loadCan} className="mt-3 flex items-center gap-2 text-xs text-[#2ec5db]" data-testid="canbus-refresh"><RefreshCw className="w-3.5 h-3.5" /> Rafraîchir</button>
          </div>
        )}

        {tab === "brut" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Extraire <b>toutes</b> les données du paquet boîtier (positions, vitesse, tensions, satellites, GSM, <b>tous les I/O</b> dont iButton/RFID, CAN, conducteur) des dernières 24 h. Le CSV inclut une colonne par I/O (ex. <span className="data">io_78</span> = iButton).</p>
            <div className="flex gap-2">
              <button onClick={() => exportData("csv")} data-testid="export-csv" className="flex-1 flex items-center gap-2 justify-center bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-2.5 rounded-md text-sm"><Download className="w-4 h-4" /> Exporter CSV</button>
              <button onClick={() => exportData("json")} data-testid="export-json" className="flex-1 flex items-center gap-2 justify-center bg-secondary/60 hover:bg-secondary border border-border font-semibold py-2.5 rounded-md text-sm"><Download className="w-4 h-4" /> Exporter JSON</button>
            </div>
            <button onClick={loadLastPacket} data-testid="view-last-packet" className="w-full flex items-center gap-2 justify-center bg-secondary/40 hover:bg-secondary border border-border font-semibold py-2.5 rounded-md text-sm"><Radio className="w-4 h-4 brand-teal" /> Afficher le dernier paquet complet</button>
            {lastPacket && (
              <div data-testid="raw-packet" className="rounded-md border border-border bg-secondary/30 p-3 text-xs">
                <p className="label-mono mb-2">Dernier paquet — {noumea(lastPacket.timestamp)}</p>
                {lastPacket.driver_tag && (
                  <p className="text-[#F5821F] font-semibold mb-1.5" data-testid="raw-ibutton">Tag iButton/RFID : {lastPacket.driver_tag}{lastPacket.driver_name ? ` → ${lastPacket.driver_name}` : " (non enregistré)"}</p>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 data max-h-56 overflow-y-auto">
                  <div>lat: <span className="text-foreground">{lastPacket.lat}</span></div>
                  <div>lon: <span className="text-foreground">{lastPacket.lon}</span></div>
                  <div>vitesse: <span className="text-foreground">{lastPacket.speed}</span></div>
                  <div>contact: <span className="text-foreground">{String(lastPacket.ignition)}</span></div>
                  <div>satellites: <span className="text-foreground">{lastPacket.satellites}</span></div>
                  <div>GSM: <span className="text-foreground">{String(lastPacket.gsm_signal)}</span></div>
                  {Object.entries(lastPacket.io || {}).map(([k, v]) => (
                    <div key={k} className={k === "78" || k === "207" ? "text-[#F5821F]" : ""}>io {k}{IO_NAMES[k] ? ` (${IO_NAMES[k]})` : ""}: <span className="text-foreground">{String(v)}</span></div>
                  ))}
                </div>
                {(!lastPacket.io || Object.keys(lastPacket.io).length === 0) && (
                  <p className="text-muted-foreground mt-2">Aucun I/O dans ce paquet. Si l'iButton n'apparaît pas, envoyez la commande « Lire / activer iButton » (onglet Commandes) et vérifiez la config I/O du boîtier.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {cutDialog && <EngineCutDialog vehicle={vehicle} action={cutDialog} onConfirm={confirmCut} onClose={() => setCutDialog(null)} />}
    </div>
  );
}
